<template>
  <ais-instant-search
    :search-client="algolia.searchClient"
    :index-name="algolia.indexProducts"
    :insights="true"
    :class="[
      `show-tier${currentTier}`,
      { [$class('loading')]: state === 'loading' },
    ]"
    :class-names="{
      'ais-InstantSearch': 'menu-drawer critical-component-hide',
    }"
    js-overlay="scrollElement"
  >
    <ais-configure
      :query="query"
      :hits-per-page.camel="7"
      :faceting-after-distinct.camel="true"
      :distinct="true"
    />

    <!-- Logo, close icon and search box -->
    <div class="menu-drawer__header">
      <transition
        name="fade-down"
        mode="out-in"
      >
        <a
          v-if="currentTier === 1 && !searchFocus && !searchActive"
          class="menu-drawer__logo"
          :href="$variable('routes.root')"
          :aria-label="$string('general.site_header.return_home')"
        >
          <div
            class="menu-drawer__logo-image"
            v-html="$variable('store.logo_secondary')"
          />
        </a>

        <button
          v-else-if="currentTier > 1"
          v-trigger-icon
          class="menu-drawer__back"
          :aria-label="`
            ${$string('general.menu_drawer.back_to')}
            ${$string('general.menu_drawer.top_level')}
          `"
          type="button"
          @click.prevent="() => currentLink = false"
        >
          <animated-icon
            :transition-on-hover="false"
            transition-type="swipe"
          >
            <template #iconOne>
              <icon-arrow-left />
            </template>

            <template #iconTwo>
              <icon-arrow-left />
            </template>
          </animated-icon>

          <span
            class="menu-drawer__back-label text-body-xs"
            v-text="currentLink.title"
          />
        </button>

        <button
          v-else-if="searchFocus || searchActive"
          v-trigger-icon
          class="menu-drawer__back"
          :aria-label="`
            ${$string('general.menu_drawer.back_to')}
            ${$string('general.menu_drawer.top_level')}
          `"
          type="button"
          @click.prevent="closeSearch"
        >
          <animated-icon
            :transition-on-hover="false"
            transition-type="swipe"
          >
            <template #iconOne>
              <icon-arrow-left />
            </template>

            <template #iconTwo>
              <icon-arrow-left />
            </template>
          </animated-icon>

          <span
            class="menu-drawer__back-label text-body-xs"
            v-text="'Menu'"
          />
        </button>
      </transition>

      <button
        v-trigger-icon
        class="menu-drawer__close"
        @click.prevent="closeOverlay(namespace)"
      >
        <animated-icon
          :transition-on-hover="false"
          transition-type="spin-90"
        >
          <template #iconOne>
            <icon-close />
          </template>

          <template #iconTwo>
            <icon-close />
          </template>
        </animated-icon>
      </button>
    </div>

    <div
      v-if="currentTier === 1"
      class="menu-drawer__search"
      :class="{ [$class('active')]: (searchActive || searchFocus) }"
    >
      <predictive-search-bar
        ref="predictiveSearch"
        @search-active="searchChange"
        @search-focussed="searchFocussed"
      />
    </div>

    <transition
      name="fade-up"
      mode="out-in"
    >
      <btn
        v-if="resultsCount > 0 && query"
        class="menu-drawer__results-button"
        modifiers="boxed"
        :icon="false"
        :label="$string('search.view_all_results', {
          pluralise: resultsCount,
          replace: { count: resultsCount }
        })"
        :url="getSearchUrl(query)"
      />
    </transition>

    <transition
      name="fade-up"
      mode="out-in"
      appear
    >
      <div class="menu-drawer__content">
        <div
          v-show="!searchActive && !searchFocus"
          class="menu-drawer__navigation-wrapper"
          js-body-scroll-lock-ignore
        >
          <!-- Upper main navigation list -->
          <div class="menu-drawer__navigation">
            <nav
              class="menu-drawer__menus"
              :aria-label="$string('accessibility.sign_posts.nav_drawer_menus')"
            >
              <!-- Tier 1 -->
              <ul
                class="menu-drawer__menu menu-drawer__tier1"
                :class="{ [$class('active')]: getActiveState(false) }"
              >
                <li
                  v-for="(tier1, index) in navigation"
                  :ref="(args) => setItemRefs(args, { tier: 1, type: 'link' })"
                  :key="tier1.handle"
                  class="menu-drawer__menu-item"
                >
                  <button
                    v-if="tier1.links.length"
                    v-trigger-icon
                    class="
                      menu-drawer__link
                      menu-drawer__button
                      text-body-xs
                    "
                    :tabindex="getActiveState(false) ? 0 : -1"
                    :aria-label="`${$string('accessibility.open')} ${tier1.title}`"
                    @click="handleLinkClick(tier1)"
                  >
                    {{ tier1.title }}
                    <animated-icon
                      :transition-on-hover="false"
                      transition-type="swipe-right"
                    >
                      <template #iconOne>
                        <icon-arrow-right />
                      </template>

                      <template #iconTwo>
                        <icon-arrow-right />
                      </template>
                    </animated-icon>
                  </button>

                  <a
                    v-else
                    class="menu-drawer__link text-body-xs"
                    :class="{'menu-drawer__link--is-sale' : addSaleHighlight && index === (navigation.length - 1)}"
                    :href="tier1.localisedUrl"
                    :tabindex="getActiveState(false) ? 0 : -1"
                    v-html="getTier1LinkText(tier1, index)"
                  />

                  <!-- Tier 2 -->
                  <ul
                    v-if="tier1.links.length"
                    class="menu-drawer__menu menu-drawer__tier2"
                    :class="{ 'is-active': getActiveState(tier1) || getChildActiveState(tier1), 'menu-drawer__tier2--multiple': currentLink.columnMultiple }"
                  >
                    <li
                      v-for="(tier2) in tier1.links"
                      :key="tier2.handle"
                      :ref="(args) => setItemRefs(args, {
                        handle: tier1.handle,
                        tier: 2,
                        type: 'link',
                      })"
                      class="menu-drawer__menu-item"
                    >
                      <menu-drawer-linklist
                        :linklist="tier2"
                      />
                    </li>
                  </ul>
                </li>
              </ul>

              <ul
                v-if="state === 'loading'"
                class="menu-drawer__loading-links"
              >
                <li
                  v-for="(item, index) in 5"
                  :key="index"
                  class="menu-drawer__menu-item"
                  :class="{[$class('loading')]: navigation.length}"
                />
              </ul>
            </nav>

            <transition
              name="fade-up-only"
              mode="out-in"
            >
              <div
                v-if="showNavBlocks"
                class="menu-drawer__nav-blocks"
              >
                <a
                  v-for="(block, index) in currentImages[getCurrentLinkReference]"
                  :key="index"
                  :href="block.link"
                  class="menu-drawer__nav-block"
                >
                  <div class="menu-drawer__nav-block-holder">
                    <responsive-image
                      :image="{
                        src: block.src,
                      }"
                      ratio="4:3"
                      :alt="`${$string('accessibility.door_tile')} ${block.text}`"
                      block-class="menu-drawer__nav-block-image"
                    />
                    <div class="menu-drawer__nav-block-overlay" />
                  </div>

                  <div class="menu-drawer__nav-block-title-wrapper ">
                    <span
                      class="menu-drawer__nav-block-title text-body-s"
                      v-text="block.text"
                    />
                  </div>
                </a>
              </div>
            </transition>
          </div>

          <transition
            name="fade-up-only"
            mode="out-in"
          >
            <market-switcher-trigger
              v-if="currentTier === 1 && (!searchActive || !searchFocus)"
              class="menu-drawer__market-switcher"
              layout="large"
            />
          </transition>

          <!-- Lower nav list -->
          <transition
            name="fade-up-only"
            mode="out-in"
          >
            <div
              v-if="currentTier === 1 && (!searchActive || !searchFocus)"
              class="menu-drawer__navigation-lower"
            >
              <responsive-image
                :alt="`${$string('accessibility.door_tile')}`"
                :image="{
                  src: drawerLowerImage
                }"
                block-class="menu-drawer"
                :padding="false"
              />
              <nav class="menu-drawer__lower-menus">
                <div
                  ref="lowerMenu"
                  class="menu-drawer__menu"
                >
                  <ul>
                    <li
                      v-for="(link, index) in drawerLowerLinks"
                      :key="index"
                      class="menu-drawer__menu-item"
                    >
                      <a
                        class="menu-drawer__link text-body-xs"
                        :href="link.url"
                        :tabindex="currentTier === 1 ? 0 : -1"
                        v-text="link.title"
                      />
                    </li>
                  </ul>

                  <ul class="menu-drawer__icon-links">
                    <li
                      v-if="showDrawerAccount"
                      class="menu-drawer__menu-item menu-drawer__item-icon"
                    >
                      <a
                        v-if="$variable('customer.loggedIn')"
                        class="menu-drawer__link text-body-xs"
                        :href="$variable('routes.account.logout')"
                        :tabindex="currentTier === 1 ? 0 : -1"
                      >
                        <icon-account />
                        <span v-text="$string('general.site_header.log_out')" />
                      </a>

                      <a
                        v-else
                        class="menu-drawer__link text-body-xs"
                        :href="$variable('routes.account.url')"
                        :tabindex="currentTier === 1 ? 0 : -1"
                      >
                        <icon-account />
                        <span v-text="$string('general.site_header.log_in_register')" />
                      </a>
                    </li>
                  </ul>
                </div>
              </nav>
            </div>
          </transition>
        </div>

        <div
          v-show="searchActive || searchFocus"
          class="menu-drawer__results"
          js-body-scroll-lock-ignore
        >
          <div
            v-show="resultsCount > 0 || searchFocus"
            class="menu-drawer__results-section"
            js-body-scroll-lock-ignore
          >
            <!-- No results -->
            <div
              v-if="query.length > 0 && resultsCount === 0"
              class="menu-drawer__results-content"
            >
              <span
                class="text-body-xs"
                v-html="noResultsString"
              />

              <p
                class="text-body-xs"
                v-html="$string('search.no_results_instruction')"
              />
            </div>

            <!-- Popular searches -->
            <div
              v-show="searchFocus && !query"
              class="
                menu-drawer__suggestions
                menu-drawer__results-content
              "
            >
              <div class="menu-drawer__results-wrapper">
                <p
                  class="text-label-s menu-drawer__result-title"
                  v-text="$string('search.popular_searches')"
                />
              </div>
              <ul>
                <li
                  v-for="(suggestion, index) in popularSearchArray"
                  :key="`${suggestion}-${index}`"
                  class="menu-drawer__results-wrapper"
                >
                  <a
                    :href="getSearchUrl(suggestion)"
                    class="menu-drawer__result-link"
                  >
                    <p
                      class="menu-drawer__result text-body-2xs"
                      v-html="suggestion"
                    />
                  </a>
                </li>
              </ul>
            </div>

            <!-- Collections -->
            <div
              v-show="query && collectionsCount > 0"
              class="
              menu-drawer__collections
              menu-drawer__results-content
            "
            >
              <ais-index :index-name="algolia.indexCollections">
                <ais-configure :hits-per-page.camel="4" />
                <algolia-stats-connector @nb-hits-change="(nbHits) => nbHitsChange(nbHits, algolia.indexCollections)" />
                <ais-hits>
                  <template #default="{ items }">
                    <div
                      v-if="items.length"
                      class="text-label-s menu-drawer__results-header"
                      v-html="$string('search.collections')"
                    />

                    <ul>
                      <li
                        v-for="item in items"
                        :key="item.objectID"
                        class="menu-drawer__results-wrapper"
                      >
                        <a
                          class="
                            menu-drawer__result
                            text-body-2xs
                          "
                          :href="`${$variable('routes.collection')}/${item.handle}`"
                        >
                          <ais-highlight
                            :hit="item"
                            attribute="title"
                            highlighted-tag-name="b"
                            :class-names="{
                              'ais-Highlight': 'body-2',
                            }"
                          />
                        </a>
                      </li>
                    </ul>
                  </template>
                </ais-hits>
              </ais-index>
            </div>

            <!-- Products / Trending Products -->
            <div
              v-show="query || (searchFocus && !query)"
              class="
                menu-drawer__products
                menu-drawer__results-content
              "
            >
              <ais-index :index-name="algolia.indexProducts">
                <ais-configure :filters="algolia.defaultFilters" />
                <algolia-stats-connector @nb-hits-change="(nbHits) => nbHitsChange(nbHits, algolia.indexProducts)" />
                <ais-query-rule-custom-data :transform-items="transformCustomData" />

                <ais-hits :transform-items="transformItems">
                  <template #default="{ items, sendEvent }">
                    <p
                      v-if="items.length"
                      class="text-label-s menu-drawer__results-header"
                      v-html="getProductsTitle()"
                    />

                    <ul>
                      <li
                        v-for="item in items"
                        :key="item.objectID"
                        class="menu-drawer__results-product-wrapper"
                      >
                        <predictive-search-card
                          :product="item"
                          @click="sendEvent('click', item, 'Product Clicked')"
                        />
                      </li>
                    </ul>
                  </template>
                </ais-hits>
              </ais-index>
            </div>

            <!-- Pages -->
            <div
              v-show="query"
              class="
                menu-drawer__pages
                menu-drawer__results-content
              "
            >
              <ais-index :index-name="algolia.indexPages">
                <ais-configure :hits-per-page.camel="3" />
                <algolia-stats-connector @nb-hits-change="(nbHits) => nbHitsChange(nbHits, algolia.indexPages)" />
                <ais-hits>
                  <template #default="{ items }">
                    <div
                      v-if="items.length"
                      class="text-label-s menu-drawer__results-header"
                      v-html="$string('search.pages')"
                    />

                    <ul>
                      <li
                        v-for="item in items"
                        :key="item.objectID"
                        class="menu-drawer__results-wrapper"
                      >
                        <a
                          class="menu-drawer__result text-body-2xs"
                          :href="`/blogs/${item?.blog?.handle}/${item.handle}`"
                        >
                          <ais-highlight
                            :hit="item"
                            attribute="title"
                            highlighted-tag-name="b"
                            :class-names="{
                              'ais-Highlight': 'text-body-2xs',
                            }"
                          />
                        </a>
                      </li>
                    </ul>
                  </template>
                </ais-hits>
              </ais-index>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </ais-instant-search>
</template>

<script>

/**
 * Vue: Menu drawer
 * -----------------------------------------------------------------------------
 * Mobile menu drawer.
 * - CSS variable --menu-drawer-links-height is updated to match height of current
 *   visible links.
 *
 * @param {String} activeMenu - Name of the active menu.
 * @param {String}  drawerLowerImage - URL for the lower drawer image.
 * @param {Array} drawerLowerLinks - Array of link objects.
 * @param {String} handle - Mobile linklist handle.
 * @param {Array} images - Array of image objects.
 * @param {Boolean} isActive - If the containing overlay is active.
 * @param {Number} itemCount - Number of tier 1 items in linklist.
 * @param {String} namespace - Overlay namespace for the drawer.
 * @param {Boolean} addSaleHighlight - Decides whether to highlight the last link.
 * @param {Boolean} showDrawerAccount - Show the account link in the drawer.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import algoliasearch from 'algoliasearch/lite'
import {
  AisInstantSearch,
  AisConfigure,
  AisHits,
  AisIndex,
  AisHighlight,
  AisQueryRuleCustomData,
} from 'vue-instantsearch/vue3/es'

import AlgoliaStatsConnector from '~async/algolia/algolia-stats-connector'

import IconArrowLeft from '~icons/direction/arrow/left.svg'
import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconClose from '~icons/misc/close.svg'
import IconAccount from '~icons/misc/account.svg'

import menu from '~/graphql/queries/menu.gql'
import { formatMenu } from '~/helpers/format'
import { formatAlgoliaProduct } from '~/helpers/format-algolia'

import AnimatedIcon from '~global/animated-icon/animated-icon'
import MarketSwitcherTrigger from '~async/market-switcher-trigger/market-switcher-trigger'
import MenuDrawerLinklist from './menu-drawer-linklist'
import ResponsiveImage from '~global/images/responsive-image'
import PredictiveSearchBar from '~async/predictive-search-bar/predictive-search-bar'
import PredictiveSearchCard from '~async/predictive-search-bar/predictive-search-card'

export default {
  name: 'MenuDrawer',

  components: {
    AisConfigure,
    AisHighlight,
    AisHits,
    AisIndex,
    AisInstantSearch,
    AisQueryRuleCustomData,
    AlgoliaStatsConnector,
    AnimatedIcon,
    IconAccount,
    IconArrowLeft,
    IconArrowRight,
    IconClose,
    MarketSwitcherTrigger,
    MenuDrawerLinklist,
    PredictiveSearchBar,
    PredictiveSearchCard,
    ResponsiveImage,
  },

  props: {
    activeMenu: {
      type: String,
      default: null,
    },
    drawerLowerImage: {
      type: String,
      default: '',
    },
    drawerLowerLinks: {
      type: Array,
      default: () => [],
    },
    handle: {
      type: String,
      default: 'main-menu',
    },
    images: {
      type: Array,
      default: () => [],
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    itemCount: {
      type: Number,
      default: 5,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    popularSearches: {
      type: Array,
      default: () => [],
    },
    addSaleHighlight: {
      type: Boolean,
      default: false,
    },
    showDrawerAccount: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['clear-active-menu'],

  data() {
    return {
      currentLink: false,
      currentHeight: false,
      itemRefs: [],
      navigation: [...Array(this.itemCount)].map((_, index) => {
        return {
          handle: `placeholder-${index}`,
          links: [],
          title: '',
          url: cnvs.routes.root,
        }
      }),
      state: 'loading',
      searchActive: false,
      searchFocus: false,
      showNavBlocks: false,
      query: '',
      collectionsCount: 0,
      pagesCount: 0,
      productsCount: 0,
      algolia: {
        indexProducts: this.$variable('algolia.indexes.products'),
        indexCollections: this.$variable('algolia.indexes.collections'),
        indexPages: this.$variable('algolia.indexes.pages'),
        defaultFilters: this.$variable('algolia.defaultFilters'),
        searchClient: algoliasearch(
          this.$variable('algolia.credentials.appId'),
          this.$variable('algolia.credentials.apiKey'),
        ),
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Finds and returns a useable reference for matching nav images with the
     * linklist they refer to.
     * @returns {String}
     */
    getCurrentLinkReference() {
      return this?.currentLink?.hrefRef || this?.currentLink?.title
    },

    /**
     * Builds a results string and makes the query bold.
     */
    noResultsString()  {
      const query = `<strong>${this.query}</strong>`

      return this.$string('search.predictive_no_results', {
        replace: { query },
      })
    },

    /**
     * Returns the total number of results.
     * @returns {Number} - Total search results.
     */
    resultsCount() {
      return this.productsCount + this.collectionsCount + this.pagesCount
    },

    /**
     * Formats and returns popular search terms.
     * @return {Array} - Array of popular search terms.
     */
    popularSearchArray() {
      if (!this.popularSearches.length)  {
        return []
      }

      return this.popularSearches.map(search => search.trim())
    },

    /**
     * Format supplied images into a format that can be easily rendered when the
     * menu is built.
     * @returns {Object} - Object of arrays with linklist titles as keys.
     */
    currentImages() {
      if  (!this.images.length)  {
        return
      }

      const formatted = this.images.reduce((agg, item) => {
        const title = item.hrefRef ? item.hrefRef : item.title
        if(agg[title])  {
          agg[title].push(item.image)
        } else {
          agg[title] = [item.image]
        }

        return agg
      }, {})
      return formatted
    },

    /**
     * Compute current item refs in current link.
     * @returns {Array}
     */
    currentItemRefs() {
      if (this.currentLink) {
        return this.itemRefs.filter((itemRef) => {
          return itemRef.handle === this.currentLink.handle
        })
      }

      return this.itemRefs.filter((itemRef) => itemRef.tier === 1)
    },

    /**
     * Compute current tier to show.
     * @returns {Number}
     */
    currentTier() {
      if (!this.currentLink) {
        return 1
      }

      return this.currentLink.tier + 1
    },
  },

  watch: {
    activeMenu(val) {
      if (!val) {
        return
      }

      this.initDrawer()
    },

    isActive() {
      if(this.isActive) {
        this.query = ''
        this.$refs.predictiveSearch?.clearSearch()
      }
    },

    /**
     * Watch the current tier change, which indicates the user has progressed
     * further into the menu. Use this to re-run the calculations needed for
     * building out the adaptive menu drawer.
     */
    currentTier: function()  {
      this.updateSizes()
      this.updateLowerMenuHeight()

      if (this.currentTier > 1 && this.currentImages) {
        setTimeout(() => {
          this.showNavBlocks = true
        }, this.$timing('normal'))
      } else if (this.currentTier <= 1 || this.currentImages) {
        this.showNavBlocks = false
      }
    },

    /**
     * Watch current link to update focus.
     */
    currentLink() {
      this.updateFocus()
      this.updateSizes()
      this.updateLowerMenuHeight()
    },

    /**
     * Watch active overlay to reset current link.
     */
    getActiveOverlay(val) {
      this.updateLowerMenuHeight()
      if (val) { return }

      this.$emit('clear-active-menu')
      this.currentLink = false
      this.closeSearch()
    },
  },

  mounted() {
    this.initDrawer()
  },

  beforeUpdate() {
    this.itemRefs = []
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPlacements: 'nosto/setPlacements',
    }),

    /**
     * Transform items to properly pass props to the product card component.
     * @returns {Array}
     */
    transformItems(items) {
      const data = items.map(item => formatAlgoliaProduct(item))
      return data
    },

    /**
     * Process the custom query data from Algolia.
     * - Used to process redirects via the search bar.
     * @param {Array} items - Algolia custom data items.
     */
    transformCustomData(items) {
      const redirectData = items.find((item) => Boolean(item.redirect))

      if (redirectData?.redirect?.force && redirectData?.redirect?.url) {
        window.location.href = redirectData.redirect.url
        return []
      }
    },

    /**
     * Set number of hits per algolia index.
     * @param {Number} nbHits - number of hits/results.
     * @param {String} algoliaIndex - Algolia index name.
     */
    nbHitsChange(nbHits, algoliaIndex) {
      switch (algoliaIndex) {
        case this.algolia.indexProducts:
          this.productsCount = nbHits
          break
        case this.algolia.indexCollections:
          this.collectionsCount = nbHits
          break
        case this.algolia.indexPages:
          this.pagesCount = nbHits
          break
      }
    },

    getProductsTitle() {
      if(this.query) {
        return this.$string('search.products')
      }

      if(this.searchFocus && !this.query) {
        return this.$string('search.trending_products')
      }
    },

    /**
     * Handles drawer init functionality.
     */
    async initDrawer()  {
      const start = performance.now()
      await this.queryMenu()
      this.removeStaticMenu()
      this.updateFocus()

      if (this.activeMenu)  {
        this.handleLinkClick({}, true)
      }
      const end = performance.now()

      /**
       * Drawer animation must finish before calculating heights.
       * - Wait minimum amount of time based on how long querying the menu took.
       */
      const waitTime = Math.abs(this.$timing('normal') - (end - start))

      setTimeout(() => {
        this.updateSizes()
        this.updateLowerMenuHeight()
        this.state = 'ready'
      }, waitTime)
    },

    /**
     * Creates a formatted serach URL.
     * @param {String} query - Search query string.
     */
    getSearchUrl(query) {
      const searchRoute = window.cnvs.routes.search || '/search'

      return `${searchRoute}?query=${query}`
    },

    /**
     * Closes the search feature.
     */
    closeSearch() {
      this.searchFocus = false
      this.searchActive = false
      this.query = ''
      this.$refs.predictiveSearch?.clearSearch()
    },

    /**
     * Updates the current search status.
     * @param {Boolean} val - Search status.
     */
    searchFocussed(val)  {
      this.searchFocus = val
    },

    /**
     * Handles updating the inputted search query.
     * @param {String} val - Current query.
     */
    searchChange(val)  {
      this.searchActive = val ? true : false
      this.query = val
    },

    /**
     * Get tier 1 link text or loading placeholder based on state.
     * @param {Object} tier - Tier 1 object.
     * @param {Number} index - For loop index.
     * @returns {String}
     */
    getTier1LinkText(tier, index) {
      if (this.state === 'loading') {
        const animationDelay = `animation-delay: ${this.$timing('quick', { index, unit: 's' })}`
        return `<span class="menu-drawer__loading" style="${animationDelay}"></span>`
      }

      return tier.title
    },

    /**
     * Get active state for nested menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getActiveState(link) {
      return this.isActive && link.handle === this.currentLink.handle
    },

    /**
     * Get active state of current nested menu's child menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getChildActiveState(link) {
      const tier3LinkActive = link.links.find((tier3Link) => {
        return tier3Link.handle === this.currentLink.handle
      })

      return Boolean(tier3LinkActive)
    },

    /**
     * Set item refs.
     * @param {HTMLElement} element - Item in v-for.
     * @param {String} data.handle - Handle of item.
     * @param {Number} data.tier - Tier of item.
     * @param {String} data.type - Type of item, `link` or `back`.
     */
    setItemRefs(element, data) {
      if (!element) {
        return
      }

      this.itemRefs.push({
        ...data,
        element,
      })
    },

    /**
     * Loads menu from GraphQL.
     * @returns {Promise}
     */
    async queryMenu() {
      return new Promise(async (resolve, reject) => {
        if (!this.handle) {
          resolve()
          return
        }

        try {
          const response = await cnvs.Query({
            query: menu,
            variables: {
              country: cnvs.store.country,
              handle: this.handle,
              language: cnvs.store.language,
            },
          })

          if (cnvs.settings.disableComponentReadyState) {
            resolve()
            return
          }

          /**
           * Detect whether the linklist has the column indicator in the title.
           */
          this.addColumnsProperty(response)

          this.navigation = formatMenu({ response })
          resolve()
        } catch (error) {
          reject()
          reportError('Failed to load menu', error)
        }
      })
    },

    /**
     * Strip out the columns property from the list title and set the applicable
     * key on the nav item object.
     * @param {Array} data - Array of linklist items returned from shopify.
     * @returns {Array} - Array of linklist items.
     */
    addColumnsProperty(data) {
      const links = data.menu?.items

      if  (!links)  {
        return data
      }

      links.forEach(link => {
        const columns = link.title.split(' || ')

        if  (columns.length === 1)  {
          link.columnMultiple = false
          return
        }

        link.columnMultiple = true
        link.title = columns[0]
      })

      return links
    },

    /**
     * Removes static menu drawer rendered by menu-drawer.snippet.liquid.
     * - Otherwise there would be duplicate menus.
     */
    removeStaticMenu() {
      const container = document.querySelector('[js-menu-drawer="container"]')

      if (!container) {
        return
      }

      container.remove()
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick(() => {
        cnvs.EventBus.emit('overlay:update', 'menuDrawer')
      })
    },

    /**
     * Update height of current active navigation.
     * - Goes through each item in currentItemRefs and get height of element.
     */
    updateSizes() {
      nextTick(() => {
        let height = 0
        let width = 0

        /**
         * Detect if in tier 2 and up on desktop, as calculations for height are
         * instead taken as the height of the tallest element
         */
        if  (this.currentTier === 1 || this.isMobile)  {
          this.currentItemRefs.forEach((ref) => {
            if (!ref?.element?.offsetHeight) {
              return
            }

            /**
             * If on mobile and the current menu is displayed in multiple horizontal
             * columns then calculate the total height of the container instead.
             */
            width += ref.element.offsetWidth
            if  (this.currentLink.columnMultiple)  {
              height = ref.element.closest('.menu-drawer__menu').offsetHeight
            } else {
              height += ref.element.offsetHeight
            }
          })
        } else {
          const heightsArr = []

          this.currentItemRefs.forEach((ref) => {
            if (!ref?.element?.offsetHeight || !ref?.element?.offsetWidth) {
              return
            }
            heightsArr.push(ref.element.offsetHeight)
            width += ref.element.offsetWidth
          })

          height = heightsArr.sort((a, b) => a - b)[0]
        }

        /**
         * If height is 0 then re-run function as it's a bug.
         */
        if ((height === 0 || width === 0) && this.currentItemRefs.length) {
          setTimeout(() => {
            this.updateSizes()
            return
          }, 100)
        }

        /**
         * Set CSS variables for linklist height and width.
         */
        document.documentElement.style.setProperty(
          '--menu-drawer-links-height',
          `${height}px`,
        )

        document.documentElement.style.setProperty(
          '--menu-drawer-links-width',
          `${width}px`,
        )

        /**
         * Update Vue data for easier debugging.
         */
        this.currentHeight = height
      })
    },

     /**
     * Update height of drawer lower navigation.
     * Gets the height of the rendered lower drawer menu items ul. Separated
     * out for readability as it sets a different css variable and does not require
     * to loop over anything.
     */
    updateLowerMenuHeight() {
      nextTick(() => {
        const height = this.$refs.lowerMenu?.offsetHeight || 0

        /**
         * If height is 0 then re-run function as it's a bug.
         */
        if (height === 0 && this.drawerlowerLinks?.length) {
          setTimeout(() => {
            this.updateLowerMenuHeight()
            return
          }, 100)
        }

        /**
         * Set CSS variable.
         */
        document.documentElement.style.setProperty(
          '--lower-menu-drawer-links-height',
          `${height}px`,
        )
      })
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     * @param {Boolean} [updateByProp]
     */
    handleLinkClick(link, updateByProp = false) {
      if  (!updateByProp)  {
        this.currentLink = link
        return
      }

      const active = this.navigation.find(item => item.title === this.activeMenu)
      if  (!active)  {
        return
      }
      this.currentLink = active
    },
  },
}
</script>

<style lang="scss">
@import './menu-drawer';
</style>
